import { AIChatNavButton } from '@ecomm/ai-chat/AIChatNavButton'
import { isSmbMVP } from '@ecomm/data-env-variables'
import { useLocale } from '@ecomm/data-hooks'
import type { GuidedSystemBuilderSchema } from '@ecomm/shared-components/v2/lib/GuidedSystemBuilder/schema'
import { useFocusTrap, useLockBodyScroll } from '@ecomm/shared-hooks'
import { useCameraOnlyLPCTAExperience } from '@ecomm/shared-ninetailed'
import { useBusiness } from '@ecomm/smb-components/BusinessProvider/BusinessProvider'
import type { Locale } from '@simplisafe/ewok'
import classNames from 'classnames'
import { some } from 'fp-ts/lib/Option'
import { useEffect, useRef, useState } from 'react'
import { useCallOnEvent } from '../../hooks/useCallOnEvent'
import { useIsAIChatEnabled } from '../../hooks/useIsAIChatEnabled'
import { AIChatNavExperimentWrapper } from './AIChatNavExperimentWrapper'
import { NavLink } from './NavLink'
import { NavMenu } from './NavMenu'
import { SMBHeaderToggle } from './SMBHeaderToggle'
import {
  NARROWEST_DESKTOP_SIZE,
  getCourtesyNavItems,
  getMainMenuItems
} from './data'
import type { CourtesyNavItemType } from './data'
import type { HeaderRedesignSchema } from './schema'

type SideNavProps = HeaderRedesignSchema & {
  readonly show: boolean
  readonly toggle: (track?: boolean) => void
  readonly close: () => void
  readonly top: number
  readonly areExpertsAvailable: boolean
}

function renderLink(
  item: CourtesyNavItemType,
  quoteWizard: GuidedSystemBuilderSchema,
  toggle: SideNavProps['toggle']
) {
  return item.openAIChat ? (
    // We don't need a wrapper here because `item` is defined in `getCourtesyNavItems` which uses `useIsAIChatExperiment` to conditionally return item data
    // Only the AI Chat courtesy nav item has a prop set for `openAIChat`
    <AIChatNavButton
      classes="items-center inline-flex gap-3 font-bold min-[1126px]:mb-[5px] min-[1126px]:border-b-2 min-[1126px]:font-medium min-[1126px]:hover:border-b-primary-100 w-full gap-3 !border-b-neutral-300 px-4 text-lg font-medium min-[1126px]:p-0 h-16 flex flex-row-reverse justify-between"
      key={item.text}
    />
  ) : (
    <NavLink
      className={classNames(
        'w-full gap-3 !border-b-neutral-300 px-4 text-lg font-medium text-white min-[1126px]:p-0',
        { 'h-16': !item.sideNavContent }
      )}
      customContent={item.sideNavContent}
      key={item.text}
      quoteWizard={{ ...quoteWizard }}
      text={item.text}
      toggleSideNav={some(toggle)}
      url={item.url}
    />
  )
}

export function SideNav({
  show,
  toggle,
  top,
  images,
  close,
  quoteWizard,
  areExpertsAvailable
}: SideNavProps) {
  const { isCameraOnlyVariant } = useCameraOnlyLPCTAExperience()
  const locale = useLocale()
  const ref = useRef(null)
  /* This value will be used to calculate the sidenav's height. We could use 100vh in its place but since mobile browsers
   won't give the expected value for that, we replace it with document.documentElement.clientHeight instead. It needs to be
   in the state to trigger a rerender if that value changes when resizing the window (for instance while zooming in and out)
   We initialize with 0 first. And then we update it with document.documentElement.clientHeight inside an useEffect. Why is this?
   Because document.documentElement.clientHeight is a value that is only available at runtime. By setting it to a state variable inside
   an useEffect, we can be sure it will be set after hydration is complete and therefore document.documentElement.clientHeight has a valid value.
  */
  const [clientHeight, setClientHeight] = useState(0)
  useEffect(() => {
    setClientHeight(globalThis.document?.documentElement.clientHeight)
  }, [])
  useFocusTrap(ref, show)
  useCallOnEvent(() => {
    setClientHeight(globalThis.document?.documentElement.clientHeight)
    // Close side nav, when resizing to desktop mode, to unlock the body scroll
    globalThis.document?.body.clientWidth >= NARROWEST_DESKTOP_SIZE && close()
  }, 'resize')
  useLockBodyScroll(show, false)
  const { isBusiness } = useBusiness()
  const [activeMainMenuDropdownIndex, setActiveMainMenuDropdownIndex] =
    useState(-1)
  const [activeCourtesyNavDropdownIndex, setActiveCourtesyNavDropdownIndex] =
    useState(-1)

  const isAIChatEnabled = useIsAIChatEnabled()
  const courtesyItems = getCourtesyNavItems(
    locale,
    areExpertsAvailable,
    isAIChatEnabled,
    isCameraOnlyVariant,
    isBusiness
  )

  return (
    <>
      <nav
        aria-label="side navigation"
        className={classNames(
          'absolute left-0 top-[50px] z-20 flex h-screen w-9/12 flex-col bg-white transition-all duration-300 md:w-6/12 min-[1126px]:hidden',
          {
            'invisible -translate-x-full md:-translate-x-[442px]': !show,
            'visible translate-x-0': show
          }
        )}
        ref={ref}
      >
        <div
          className="flex w-full flex-col flex-nowrap justify-between overflow-y-auto"
          style={{
            height: `calc(${clientHeight}px + ${globalThis.document?.documentElement.scrollTop}px - ${top}px - 50px)`
          }}
        >
          <MainMenuList
            locale={locale}
            isCameraOnlyVariant={isCameraOnlyVariant}
            isBusiness={isBusiness}
            images={images}
            quoteWizard={quoteWizard}
            toggle={toggle}
            activeMainMenuDropdownIndex={activeMainMenuDropdownIndex}
            setActiveMainMenuDropdownIndex={setActiveMainMenuDropdownIndex}
            setActiveCourtesyNavDropdownIndex={
              setActiveCourtesyNavDropdownIndex
            }
          />
          <div className="bg-neutral-black w-full">
            {courtesyItems
              .filter(item => item.displayInSideNav)
              .map((item, i) =>
                item.hasDropdown && !item.sideNavContent ? (
                  <NavMenu
                    displayInSideNav={item.displayInSideNav}
                    dropdownMenu={item.dropdownMenu}
                    images={images}
                    key={`${item.text}-${item.displayInSideNav}`}
                    onToggleDropdown={(close?: boolean) => {
                      close && toggle()
                      !close &&
                        setActiveCourtesyNavDropdownIndex(
                          activeCourtesyNavDropdownIndex === i ? -1 : i
                        )
                      setActiveMainMenuDropdownIndex(-1)
                    }}
                    quoteWizard={quoteWizard}
                    showDropdown={activeCourtesyNavDropdownIndex === i}
                    showOverlay={false}
                    sideNav={true}
                    text={item.text}
                  />
                ) : (
                  renderLink(item, quoteWizard, toggle)
                )
              )}
          </div>
        </div>
      </nav>
      <div
        className={classNames(
          'absolute left-0 top-[50px] z-[9] h-screen w-full bg-black transition-opacity duration-300 min-[1126px]:hidden',
          show ? 'opacity-40' : 'pointer-events-none opacity-0'
        )}
        data-testid="sidenav-background"
        onClick={() => {
          toggle(true)
        }}
      />
      <AIChatNavExperimentWrapper />
    </>
  )
}

type MainMenuListProps = {
  readonly locale: Locale
  readonly isCameraOnlyVariant: boolean
  readonly isBusiness: boolean
  readonly images: SideNavProps['images']
  readonly quoteWizard: SideNavProps['quoteWizard']
  readonly toggle: (close?: boolean) => void
  readonly activeMainMenuDropdownIndex: number
  readonly setActiveMainMenuDropdownIndex: (index: number) => void
  readonly setActiveCourtesyNavDropdownIndex: (index: number) => void
}

function MainMenuList({
  locale,
  isCameraOnlyVariant,
  isBusiness,
  images,
  quoteWizard,
  toggle,
  activeMainMenuDropdownIndex,
  setActiveMainMenuDropdownIndex,
  setActiveCourtesyNavDropdownIndex
}: MainMenuListProps) {
  return (
    <div>
      {isSmbMVP ? <SMBHeaderToggle /> : null}
      {getMainMenuItems(locale, isCameraOnlyVariant, isBusiness).map(
        (item, i) =>
          item.hasDropdown ? (
            <NavMenu
              dropdownMenu={item.dropdownMenu}
              images={images}
              key={item.text}
              mobileIcon={item.mobileIcon}
              onToggleDropdown={(close?: boolean) => {
                close && toggle()
                !close &&
                  setActiveMainMenuDropdownIndex(
                    activeMainMenuDropdownIndex === i ? -1 : i
                  )
                setActiveCourtesyNavDropdownIndex(-1)
              }}
              quoteWizard={quoteWizard}
              showDropdown={activeMainMenuDropdownIndex === i}
              showOverlay={false}
              sideNav={true}
              text={item.text}
            />
          ) : (
            <NavLink
              key={item.text}
              mobileIcon={item.mobileIcon}
              openQuiz={item.openQuiz}
              quoteWizard={{ ...quoteWizard }}
              text={item.text}
              toggleSideNav={some(toggle)}
              url={item.url}
            />
          )
      )}
    </div>
  )
}
